import { useCallback, useState } from "react";
import { mergeAccompanyingPersonsWithReloadedFees } from "../registration/registrationSync";

const sameFeeType = (left, right) => String(left) === String(right);

const buildAccompanyingPersonsFromFeeCollections = ({
  additionalFees = [],
  zeroPayments = [],
  accompanyingPersonFee,
}) => {
  if (!accompanyingPersonFee) {
    return [];
  }

  const accompanyingPersonsFromAdditionalFees = additionalFees
    .filter((fee) => sameFeeType(fee.feeTypeId, accompanyingPersonFee.id))
    .map((fee) => ({
      name: fee.name,
      paymentId: fee.paymentId,
      paid: !!fee.paymentId,
      feeTypeId: fee.feeTypeId,
      id: fee.id,
    }));

  const accompanyingPersonsFromZeroPayments = zeroPayments
    .filter((zeroPayment) =>
      sameFeeType(zeroPayment.feeTypeId, accompanyingPersonFee.id)
    )
    .map((zeroPayment) => ({
      name: zeroPayment.name,
      paymentId: null,
      paid: true,
      feeTypeId: zeroPayment.feeTypeId,
      id: zeroPayment.id,
    }));

  return [
    ...accompanyingPersonsFromAdditionalFees,
    ...accompanyingPersonsFromZeroPayments,
  ];
};

export const useAccompanyingPersons = ({ setRegistrationJustUpdated }) => {
  const [accompanyingPersons, setAccompanyingPersons] = useState([]);
  const [accompanyingPersonFee, setAccompanyingPersonFee] = useState(null);

  const handleAddAccompanyingPerson = () => {
    setAccompanyingPersons((currentPersons) => [
      ...currentPersons,
      {
        name: "",
        paid: false,
        paymentId: null,
        feeTypeId: accompanyingPersonFee?.id,
      },
    ]);
    setRegistrationJustUpdated(false);
  };

  const handleRemoveAccompanyingPerson = (index) => {
    setAccompanyingPersons((currentPersons) =>
      currentPersons.filter((_, currentIndex) => currentIndex !== index)
    );
    setRegistrationJustUpdated(false);
  };

  const handleAccompanyingPersonNameChange = (index, value) => {
    setAccompanyingPersons((currentPersons) =>
      currentPersons.map((person, currentIndex) =>
        currentIndex === index ? { ...person, name: value } : person
      )
    );
    setRegistrationJustUpdated(false);
  };

  const restoreAccompanyingPersonsFromFees = useCallback(
    ({ additionalFees = [], zeroPayments = [], replaceExisting = false }) => {
      if (!accompanyingPersonFee) {
        return [];
      }

      const restoredPersons = buildAccompanyingPersonsFromFeeCollections({
        additionalFees,
        zeroPayments,
        accompanyingPersonFee,
      });

      if (
        restoredPersons.length > 0 &&
        (replaceExisting || accompanyingPersons.length === 0)
      ) {
        setAccompanyingPersons(restoredPersons);
      }

      return restoredPersons;
    },
    [accompanyingPersonFee, accompanyingPersons.length]
  );

  const syncAccompanyingPersonsWithReloadedRegistration = useCallback(
    (reloadedRegistration) => {
      if (!accompanyingPersonFee) {
        return accompanyingPersons;
      }

      const updatedAccompanyingPersons = mergeAccompanyingPersonsWithReloadedFees({
        accompanyingPersons,
        accompanyingPersonFee,
        reloadedRegistration,
      });

      setAccompanyingPersons(updatedAccompanyingPersons);
      return updatedAccompanyingPersons;
    },
    [accompanyingPersons, accompanyingPersonFee]
  );

  return {
    accompanyingPersons,
    setAccompanyingPersons,
    accompanyingPersonFee,
    setAccompanyingPersonFee,
    handleAddAccompanyingPerson,
    handleRemoveAccompanyingPerson,
    handleAccompanyingPersonNameChange,
    restoreAccompanyingPersonsFromFees,
    syncAccompanyingPersonsWithReloadedRegistration,
  };
};
